import { useCallback, useEffect, useState } from "react";
import { getAllPaymentMethods } from "../services/api";
import type {
  IAllPaymentMethods,
  IBankAccountPaymentMethod,
  ICardPaymentMethod,
  ILastUsedFunding,
} from "../interfaces/payment-method";
import { useStore } from "../store/globalState";
import { paymentMethodListSettled } from "../utils/persistFundingSelection";

/**
 * Module-level cache for the user's payment methods. It lets us warm the data
 * on the transfer page (mount) so the "select payment method" popup can render
 * its list instantly instead of fetching on open — which made the list "pop in"
 * and shift the layout once the request resolved.
 */
let cache: { linkId: string; data: IAllPaymentMethods } | null = null;
type TrackedPaymentMethods = Promise<IAllPaymentMethods> & { seq: number };

let inflight: { linkId: string; promise: TrackedPaymentMethods } | null = null;
/** Latest fetch wins. An older response must not put the pre-register list back. */
let requestSeq = 0;

const fetchPaymentMethods = (
  linkId: string,
  force = false
): TrackedPaymentMethods => {
  if (!force && cache && cache.linkId === linkId) {
    return Object.assign(Promise.resolve(cache.data), { seq: requestSeq });
  }
  if (!force && inflight && inflight.linkId === linkId) {
    return inflight.promise;
  }

  const seq = ++requestSeq;
  const promise = getAllPaymentMethods(linkId)
    .then((data) => {
      if (seq === requestSeq) {
        cache = { linkId, data };
      }
      if (inflight?.promise === promise) inflight = null;
      return data;
    })
    .catch((err) => {
      if (inflight?.promise === promise) inflight = null;
      throw err;
    });

  const tracked = Object.assign(promise, { seq });
  inflight = { linkId, promise: tracked };
  return tracked;
};

export interface UsePaymentMethodsResult {
  /** Enabled cards only. */
  cards: ICardPaymentMethod[];
  bankAccounts: IBankAccountPaymentMethod[];
  lastUsedFunding: ILastUsedFunding | null;
  /** True only on a cold load (no cached data to show yet). */
  loading: boolean;
  /**
   * True only after the force-refresh for the current link and cardList
   * succeeds. A warm cache does not count, and a failed refresh leaves this
   * false so Transfer does not drop a just-added card.
   */
  loaded: boolean;
  /** Force a re-fetch, ignoring the warm cache (picker open / after register). */
  refresh: () => Promise<void>;
}

/**
 * Shared, cached access to the user's payment methods. Both the transfer page
 * and the payment-method popup consume this so the network request happens once
 * and the popup opens with the list already populated.
 *
 * Re-fetches whenever the page mounts or `cardList` changes. The add-card
 * screen updates `cardList` while this page is unmounted, so a warm cache from
 * before the register must not be treated as final. `refresh` does the same
 * when the payer opens the selector.
 */
export const usePaymentMethods = (
  linkId: string | null | undefined
): UsePaymentMethodsResult => {
  const { cardList } = useStore();

  const hasCache = !!(linkId && cache && cache.linkId === linkId);
  const [data, setData] = useState<IAllPaymentMethods | null>(
    hasCache ? cache!.data : null
  );
  const [loading, setLoading] = useState(!hasCache);
  const [settled, setSettled] = useState<{
    linkId: string;
    cardList: typeof cardList;
  } | null>(null);
  const loaded = paymentMethodListSettled({ linkId, cardList, settled });

  useEffect(() => {
    if (!linkId) return;

    const cached = cache && cache.linkId === linkId ? cache.data : null;

    if (cached) {
      setData(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }

    let active = true;
    // Always bypass the warm cache. Add-card updates the list while this page
    // is unmounted, so the list from before the form must not stay on screen.
    const pending = fetchPaymentMethods(linkId, true);
    pending
      .then((res) => {
        if (!active || pending.seq !== requestSeq) return;
        setData(res);
        setLoading(false);
        setSettled({ linkId, cardList });
      })
      .catch(() => {
        if (active && pending.seq === requestSeq) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [linkId, cardList]);

  const refresh = useCallback(async () => {
    if (!linkId) return;
    const pending = fetchPaymentMethods(linkId, true);
    try {
      const res = await pending;
      if (pending.seq !== requestSeq) return;
      setData(res);
      setLoading(false);
      setSettled({ linkId, cardList });
    } catch {
      if (pending.seq === requestSeq) setLoading(false);
    }
  }, [linkId, cardList]);

  return {
    cards: (data?.cards ?? []).filter((c) => c.enabled),
    bankAccounts: (data?.bankAccounts ?? []).filter((a) => a.enabled),
    lastUsedFunding: data?.lastUsedFunding ?? null,
    loading,
    loaded,
    refresh,
  };
};
